// Show-window declaration section for the Load-in tab (house-feed
// load-baselines feature, U4). Lets an operator declare doors/curfew per
// show-day, tighten the derived window after a show, and correct doors.
//
// Times are venue-local wall-clock. Doors/curfew/started/ended on the wire
// are ISO-8601 instants the server derived in the venue timezone — this
// component renders and edits them in that same venue timezone so the
// operator always sees the clock time they entered, regardless of the
// browser's own timezone.
//
// No optimistic state (dyno-pwa convention #7): every mutation waits for
// the server response, then refetches the list.

import { Fragment, useCallback, useEffect, useMemo, useState } from "react";
import { api, ApiError, setShowStartOverride } from "@/lib/api";
import { IS_ADMIN_HOST } from "@/lib/auth";
import type { DeclaredShowPeriod, DeclaredShowPeriodsResponse, Venue } from "@/lib/types";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";

// Venue-local time helpers moved to `lib/venueTime.ts` so every surface
// (drill-down, roster panel, PQ event feed, dashboards) can share one
// source of truth. ShowWindowSection had the original implementation —
// re-exported now from the shared module via named imports above.
import {
  partsInTz,
  isoToLocalInput,
  isoToClock,
  isoToDateClock,
  localInputToIso,
  dateAndTimeToIso,
} from "@/lib/venueTime";
// Re-export so any existing same-module consumers keep working through
// the module-private symbols.
export { partsInTz, isoToLocalInput, isoToClock, isoToDateClock };
export { localInputToIso, dateAndTimeToIso };

// ---------------------------------------------------------------------------
// Lifecycle badge
// ---------------------------------------------------------------------------

type BadgeKind = "declared" | "occurred" | "tightened" | "no_show" | "excluded";

// Derive the lifecycle badge from the server-authoritative fields:
//   - excluded         -> operator manually de-selected from sizing
//   - no_show          -> auto-classified (post-doors-load check failed)
//   - tightened        -> operator narrowed the window
//   - occurred         -> sustained post-doors load observed + period ended
//   - declared         -> default lifecycle
function badgeKind(p: DeclaredShowPeriod): BadgeKind {
  if (p.show_status === "excluded") return "excluded";
  if (p.show_status === "no_show") return "no_show";
  if (p.tightened_at) return "tightened";
  const ended = new Date(p.ended_at).getTime();
  if (p.observed_duration_s != null && p.observed_duration_s > 0 && ended < Date.now()) {
    return "occurred";
  }
  return "declared";
}

const BADGE_STYLE: Record<BadgeKind, { label: string; cls: string }> = {
  declared: {
    label: "Declared",
    cls: "border-sky-500/30 bg-sky-500/10 text-sky-400",
  },
  occurred: {
    label: "Show occurred",
    cls: "border-emerald-500/30 bg-emerald-500/10 text-emerald-400",
  },
  tightened: {
    label: "Tightened",
    cls: "border-violet-500/30 bg-violet-500/10 text-violet-300",
  },
  no_show: {
    label: "No-show",
    cls: "border-amber-500/30 bg-amber-500/10 text-amber-500",
  },
  excluded: {
    label: "Excluded",
    cls: "border-zinc-500/40 bg-zinc-500/10 text-zinc-300 line-through decoration-zinc-500/60",
  },
};

function LifecycleBadge({ kind }: { kind: BadgeKind }) {
  const s = BADGE_STYLE[kind];
  return (
    <span
      className={`inline-block rounded-md border text-[10px] uppercase tracking-wide px-1.5 py-0.5 ${s.cls}`}
    >
      {s.label}
    </span>
  );
}

// ---------------------------------------------------------------------------
// Error-message helper
// ---------------------------------------------------------------------------

// The show report lives on the `map.*` subdomain (cloud-api → Caddy
// `/reports/*` is only proxied on that stanza). The dyno-pwa is served
// from `dyno.*` or `admin-dyno.*`, so we rewrite the host segment.
// Falls back to the canonical `map.overdrive.rocks` for local dev.
function showReportUrl(periodId: string): string {
  if (typeof window === "undefined") {
    return `https://map.overdrive.rocks/reports/show/${encodeURIComponent(periodId)}`;
  }
  const host = window.location.host.replace(/^(admin-)?dyno\./, "map.");
  // In local dev (host = "localhost:5173") the replace is a no-op; default
  // to the production map host so the link still works.
  const finalHost = host === window.location.host && /^(localhost|127\.0\.0\.1)/.test(host)
    ? "map.overdrive.rocks"
    : host;
  return `${window.location.protocol}//${finalHost}/reports/show/${encodeURIComponent(periodId)}`;
}


function apiErrorMessage(e: unknown, fallback: string): string {
  if (e instanceof ApiError) {
    const body = e.body as { error?: string; detail?: string } | undefined;
    if (body?.error === "tighten_discard_not_confirmed") {
      // Should not normally surface — the UI gates this — but be explicit.
      return "This correction would discard a tightened window; confirm to proceed.";
    }
    if (body?.detail) return body.detail;
    if (body?.error) return body.error.replace(/_/g, " ");
    return `Server error ${e.status}`;
  }
  return fallback;
}

// Curfew is allowed to roll past midnight — if the curfew clock reads at or
// before doors, attribute it to the following calendar day.
function resolveCurfewIso(
  showDay: string,
  curfewTime: string,
  doorsIso: string,
  tz: string,
): string | null {
  let curfewIso = dateAndTimeToIso(showDay, curfewTime, tz);
  if (!curfewIso) return null;
  if (new Date(curfewIso).getTime() <= new Date(doorsIso).getTime()) {
    const next = new Date(`${showDay}T00:00:00Z`);
    next.setUTCDate(next.getUTCDate() + 1);
    curfewIso = dateAndTimeToIso(next.toISOString().slice(0, 10), curfewTime, tz);
  }
  return curfewIso;
}

// ---------------------------------------------------------------------------
// Add-show-day form
// ---------------------------------------------------------------------------

function AddShowDayForm({
  windowId,
  venueTz,
  onAdded,
}: {
  windowId: string;
  venueTz: string;
  onAdded: () => void | Promise<void>;
}) {
  const [open, setOpen] = useState(false);
  const [showDay, setShowDay] = useState("");
  const [doorsTime, setDoorsTime] = useState("");
  // Optional pre-set actual show start. Useful when there's no doors event
  // (closed run-through, soundcheck day) and the operator wants to anchor the
  // show-start directly. When set, auto-detect skips this period.
  const [actualShowStartTime, setActualShowStartTime] = useState("");
  const [curfewTime, setCurfewTime] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const reset = () => {
    setShowDay("");
    setDoorsTime("");
    setActualShowStartTime("");
    setCurfewTime("");
    setError(null);
  };

  const submit = async () => {
    setError(null);
    if (!showDay) {
      setError("Pick a show day.");
      return;
    }
    if (!doorsTime) {
      setError("Enter a doors time.");
      return;
    }
    const doorsIso = dateAndTimeToIso(showDay, doorsTime, venueTz);
    if (!doorsIso) {
      setError("Doors time is not a valid time.");
      return;
    }
    let curfewIso: string | null = null;
    if (curfewTime.trim()) {
      curfewIso = resolveCurfewIso(showDay, curfewTime, doorsIso, venueTz);
      if (!curfewIso) {
        setError("Curfew time is not a valid time.");
        return;
      }
    }
    let actualIso: string | null = null;
    if (actualShowStartTime.trim()) {
      actualIso = dateAndTimeToIso(showDay, actualShowStartTime, venueTz);
      if (!actualIso) {
        setError("Actual show start is not a valid time.");
        return;
      }
    }
    setBusy(true);
    try {
      await api.post(`/api/windows/${encodeURIComponent(windowId)}/show-periods/declare`, {
        show_day: showDay,
        doors_at: doorsIso,
        ...(curfewIso ? { curfew_at: curfewIso } : {}),
        ...(actualIso ? { actual_show_start_ts: actualIso } : {}),
      });
      reset();
      setOpen(false);
      await onAdded();
    } catch (e) {
      // Preserve the entered values; surface the server's reason inline.
      setError(apiErrorMessage(e, "Could not declare the show day."));
    } finally {
      setBusy(false);
    }
  };

  if (!open) {
    return (
      <Button size="sm" variant="outline" onClick={() => setOpen(true)}>
        Add show day
      </Button>
    );
  }

  return (
    <div className="rounded-md border border-input bg-muted/30 p-3 space-y-3">
      <div className="flex flex-wrap items-end gap-3">
        <div className="space-y-1">
          <Label className="text-xs">Show day</Label>
          <Input
            type="date"
            value={showDay}
            disabled={busy}
            onChange={(e) => setShowDay(e.target.value)}
            className="h-9 w-40"
          />
        </div>
        <div className="space-y-1">
          <Label className="text-xs">Doors (venue-local)</Label>
          <Input
            type="time"
            value={doorsTime}
            disabled={busy}
            onChange={(e) => setDoorsTime(e.target.value)}
            className="h-9 w-32"
          />
        </div>
        <div className="space-y-1">
          <Label className="text-xs">Actual show start (optional)</Label>
          <Input
            type="time"
            value={actualShowStartTime}
            disabled={busy}
            onChange={(e) => setActualShowStartTime(e.target.value)}
            className="h-9 w-32"
          />
        </div>
        <div className="space-y-1">
          <Label className="text-xs">Curfew (optional)</Label>
          <Input
            type="time"
            value={curfewTime}
            disabled={busy}
            onChange={(e) => setCurfewTime(e.target.value)}
            className="h-9 w-32"
          />
        </div>
        <Button size="sm" disabled={busy} onClick={submit}>
          {busy ? "Declaring…" : "Declare"}
        </Button>
        <Button
          size="sm"
          variant="ghost"
          disabled={busy}
          onClick={() => {
            reset();
            setOpen(false);
          }}
        >
          Cancel
        </Button>
      </div>
      <p className="text-xs text-muted-foreground">
        The window is derived server-side: 6 hours before doors through curfew,
        or doors + 6 hours when no curfew is given. Set <em>actual show start</em>
        when there's no doors event (closed run-through, soundcheck day) — it
        anchors the show-condition math directly and disables auto-detect.
      </p>
      {error && <div className="text-destructive text-sm">{error}</div>}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Tighten dialog — narrow the derived span after a show
// ---------------------------------------------------------------------------

function TightenDialog({
  period,
  venueTz,
  onClose,
  onDone,
}: {
  period: DeclaredShowPeriod;
  venueTz: string;
  onClose: () => void;
  onDone: () => void | Promise<void>;
}) {
  const derivedStart = period.started_at;
  const derivedEnd = period.ended_at;
  const [start, setStart] = useState(() => isoToLocalInput(derivedStart, venueTz));
  const [end, setEnd] = useState(() => isoToLocalInput(derivedEnd, venueTz));
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const submit = async () => {
    setError(null);
    const startIso = localInputToIso(start, venueTz);
    const endIso = localInputToIso(end, venueTz);
    if (!startIso || !endIso) {
      setError("Both start and end must be valid times.");
      return;
    }
    const lo = new Date(derivedStart).getTime();
    const hi = new Date(derivedEnd).getTime();
    const s = new Date(startIso).getTime();
    const e = new Date(endIso).getTime();
    // Narrow-only: the server enforces this too, but reject obvious
    // out-of-bounds entries inline so the operator gets immediate feedback.
    if (s >= e) {
      setError("Start must be before end.");
      return;
    }
    if (s < lo || e > hi) {
      setError(
        `The tightened window must stay inside the derived span ` +
          `(${isoToDateClock(derivedStart, venueTz)} - ${isoToDateClock(derivedEnd, venueTz)}).`,
      );
      return;
    }
    setBusy(true);
    try {
      await api.patch(`/api/show-periods/${encodeURIComponent(period.period_id)}/tighten`, {
        started_at: startIso,
        ended_at: endIso,
      });
      onClose();
      await onDone();
    } catch (err) {
      setError(apiErrorMessage(err, "Could not tighten the window."));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Tighten the {period.show_day} window</DialogTitle>
          <DialogDescription>
            Narrow the window to the span where real show load occurred. You can
            only narrow it — the new span must stay inside the derived window
            ({isoToDateClock(derivedStart, venueTz)} - {isoToDateClock(derivedEnd, venueTz)}),
            venue-local.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <div className="space-y-2">
            <Label>Window start (venue-local)</Label>
            <Input
              type="datetime-local"
              value={start}
              disabled={busy}
              onChange={(e) => setStart(e.target.value)}
            />
          </div>
          <div className="space-y-2">
            <Label>Window end (venue-local)</Label>
            <Input
              type="datetime-local"
              value={end}
              disabled={busy}
              onChange={(e) => setEnd(e.target.value)}
            />
          </div>
          {error && <div className="text-destructive text-sm">{error}</div>}
        </div>
        <DialogFooter>
          <Button variant="outline" disabled={busy} onClick={onClose}>
            Cancel
          </Button>
          <Button disabled={busy} onClick={submit}>
            {busy ? "Tightening…" : "Tighten window"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ---------------------------------------------------------------------------
// Edit-doors dialog — correct doors/curfew, optionally discarding a tighten
// ---------------------------------------------------------------------------

function EditDoorsDialog({
  period,
  venueTz,
  onClose,
  onDone,
}: {
  period: DeclaredShowPeriod;
  venueTz: string;
  onClose: () => void;
  onDone: () => void | Promise<void>;
}) {
  const isTightened = period.tightened_at != null;
  const [doorsTime, setDoorsTime] = useState(() => isoToClock(period.doors_at, venueTz));
  const [curfewTime, setCurfewTime] = useState(() =>
    period.curfew_at ? isoToClock(period.curfew_at, venueTz) : "",
  );
  // Two-step gate: when the window is tightened, the operator must clear a
  // confirmation before the PATCH fires.
  const [confirming, setConfirming] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const send = async () => {
    setError(null);
    if (!doorsTime) {
      setError("Enter a doors time.");
      setConfirming(false);
      return;
    }
    const doorsIso = dateAndTimeToIso(period.show_day, doorsTime, venueTz);
    if (!doorsIso) {
      setError("Doors time is not a valid time.");
      setConfirming(false);
      return;
    }
    let curfewIso: string | null = null;
    if (curfewTime.trim()) {
      curfewIso = resolveCurfewIso(period.show_day, curfewTime, doorsIso, venueTz);
      if (!curfewIso) {
        setError("Curfew time is not a valid time.");
        setConfirming(false);
        return;
      }
    }
    setBusy(true);
    try {
      await api.patch(`/api/show-periods/${encodeURIComponent(period.period_id)}/doors`, {
        doors_at: doorsIso,
        ...(curfewIso ? { curfew_at: curfewIso } : {}),
        ...(isTightened ? { confirm_discard_tighten: true } : {}),
      });
      onClose();
      await onDone();
    } catch (err) {
      setError(apiErrorMessage(err, "Could not correct the doors time."));
    } finally {
      setBusy(false);
    }
  };

  const onPrimary = () => {
    // A tightened window: the first press opens the discard confirmation;
    // only the confirm action sends the PATCH (with confirm_discard_tighten).
    if (isTightened && !confirming) {
      setConfirming(true);
      return;
    }
    send();
  };

  return (
    <Dialog open onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent>
        {confirming ? (
          <>
            <DialogHeader>
              <DialogTitle>Discard the tightened window?</DialogTitle>
              <DialogDescription>
                This show window has been tightened to a narrowed span. Correcting
                the doors time re-derives the window from scratch and discards that
                tightening — you will need to tighten it again afterward. Continue?
              </DialogDescription>
            </DialogHeader>
            {error && <div className="text-destructive text-sm">{error}</div>}
            <DialogFooter>
              <Button
                variant="outline"
                disabled={busy}
                onClick={() => setConfirming(false)}
              >
                Keep tightened window
              </Button>
              <Button variant="destructive" disabled={busy} onClick={send}>
                {busy ? "Correcting…" : "Discard and correct"}
              </Button>
            </DialogFooter>
          </>
        ) : (
          <>
            <DialogHeader>
              <DialogTitle>Edit doors for {period.show_day}</DialogTitle>
              <DialogDescription>
                Correct the doors and curfew times for this show day. The derived
                window is recomputed server-side. Times are venue-local.
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4">
              <div className="space-y-2">
                <Label>Doors (venue-local)</Label>
                <Input
                  type="time"
                  value={doorsTime}
                  disabled={busy}
                  onChange={(e) => setDoorsTime(e.target.value)}
                />
              </div>
              <div className="space-y-2">
                <Label>Curfew (optional, venue-local)</Label>
                <Input
                  type="time"
                  value={curfewTime}
                  disabled={busy}
                  onChange={(e) => setCurfewTime(e.target.value)}
                />
              </div>
              {isTightened && (
                <p className="text-xs text-amber-500">
                  This window is tightened — saving a correction will discard the
                  tightened span.
                </p>
              )}
              {error && <div className="text-destructive text-sm">{error}</div>}
            </div>
            <DialogFooter>
              <Button variant="outline" disabled={busy} onClick={onClose}>
                Cancel
              </Button>
              <Button disabled={busy} onClick={onPrimary}>
                {busy ? "Saving…" : isTightened ? "Save correction…" : "Save correction"}
              </Button>
            </DialogFooter>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}

// ---------------------------------------------------------------------------
// Show-start override dialog — operator-facing UI for the U4 PATCH on
// `event_show_periods.actual_show_start_ts` (roster-infra plan U11).
//
// v1 ships 3 display states (NULL / auto-detected / operator-override). The
// "operator override" state is inferred from session state (the operator's
// own PATCH within this session) — v1's U3 schema has no
// `actual_show_start_source` column, so on page reload an operator's prior
// override appears as plain "auto-detected" with the override ts. Deferred
// follow-up: add the source column to unlock a 4-state UI.
//
// Drift indicator + [Compute now] are explicitly deferred — they depend on
// helpers v1's U3 doesn't ship (compute_show_start_preview + an
// immediate-compute endpoint).
// ---------------------------------------------------------------------------

// Render a non-NULL `actual_show_start_ts` with a "(+Xmin from doors)" hint.
// Returns just the clock label when the offset is non-positive or unknown.
function formatShowStartLabel(
  actualTs: string,
  doorsIso: string,
  venueTz: string,
  operatorOverride: boolean,
): string {
  const clock = isoToClock(actualTs, venueTz);
  const deltaMs = new Date(actualTs).getTime() - new Date(doorsIso).getTime();
  const deltaMin = Math.round(deltaMs / 60000);
  const sign = deltaMin >= 0 ? "+" : "";
  const annot = operatorOverride
    ? `${sign}${deltaMin}min from doors, operator override`
    : `${sign}${deltaMin}min from doors`;
  return `${clock} (${annot})`;
}

function ShowStartOverrideDialog({
  period,
  venueTz,
  onClose,
  onDone,
}: {
  period: DeclaredShowPeriod;
  venueTz: string;
  onClose: () => void;
  // Called on PATCH success with the new ts (null when cleared) so the parent
  // can mark this period as session-overridden and trigger a reload.
  onDone: (ts: string | null) => void | Promise<void>;
}) {
  // Pre-populate with the current override (when editing) or the doors time
  // (when first overriding from auto-detect / NULL).
  const initial = period.actual_show_start_ts ?? period.doors_at;
  const [value, setValue] = useState(() => isoToLocalInput(initial, venueTz));
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const submit = async () => {
    setError(null);
    const iso = localInputToIso(value, venueTz);
    if (!iso) {
      setError("Show-start must be a valid time.");
      return;
    }
    // Frontend mirror of the U4 backend bounds check
    // (ts ∈ [started_at, started_at + 24h]). Backend remains the ultimate
    // gate; this short-circuit avoids a round-trip on obviously-bad input.
    const lo = new Date(period.started_at).getTime();
    const hi = lo + 24 * 60 * 60 * 1000;
    const ts = new Date(iso).getTime();
    if (ts < lo || ts > hi) {
      setError(
        `Show-start must be within 24h of the window start ` +
          `(${isoToDateClock(period.started_at, venueTz)}).`,
      );
      return;
    }
    setBusy(true);
    try {
      await setShowStartOverride(period.period_id, iso);
      onClose();
      await onDone(iso);
    } catch (err) {
      setError(apiErrorMessage(err, "Could not save the show-start override."));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>
            Override show-start for {period.show_day}
          </DialogTitle>
          <DialogDescription>
            Set the moment the show actually started. The roster's Tier 1
            subset-coincidence reads this anchor when computing the
            show-aligned composite — overriding it re-anchors the math and
            evicts any cached results for this event. Times are venue-local.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <div className="space-y-2">
            <Label>Show-start (venue-local)</Label>
            <Input
              type="datetime-local"
              value={value}
              disabled={busy}
              onChange={(e) => setValue(e.target.value)}
            />
            <p className="text-xs text-muted-foreground">
              Doors: {isoToClock(period.doors_at, venueTz)} · Window:{" "}
              {isoToDateClock(period.started_at, venueTz)} -{" "}
              {isoToDateClock(period.ended_at, venueTz)}
            </p>
          </div>
          {error && <div className="text-destructive text-sm">{error}</div>}
        </div>
        <DialogFooter>
          <Button variant="outline" disabled={busy} onClick={onClose}>
            Cancel
          </Button>
          <Button disabled={busy} onClick={submit}>
            {busy ? "Saving…" : "Save override"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ---------------------------------------------------------------------------
// Section
// ---------------------------------------------------------------------------

export function ShowWindowSection({
  windowId,
  venue,
}: {
  windowId: string;
  // The active window's venue, or null when no venue is assigned.
  venue: Venue | null;
}) {
  const [periods, setPeriods] = useState<DeclaredShowPeriod[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [tightenTarget, setTightenTarget] = useState<DeclaredShowPeriod | null>(null);
  const [editDoorsTarget, setEditDoorsTarget] = useState<DeclaredShowPeriod | null>(null);
  const [overrideTarget, setOverrideTarget] = useState<DeclaredShowPeriod | null>(null);
  // Session-only tracking of which period_ids the operator has overridden
  // this session. v1 has no `actual_show_start_source` column, so this is
  // the only signal that distinguishes operator-override from auto-detect
  // in the UI. On page reload the Set is empty and prior overrides display
  // as plain auto-detected (the trade-off the plan calls out).
  const [sessionOverridden, setSessionOverridden] = useState<Set<string>>(
    () => new Set(),
  );
  // Clear-in-flight tracking for the [Clear] action (no dialog — fires the
  // PATCH inline). One-at-a-time gate avoids double-clicks.
  const [clearingId, setClearingId] = useState<string | null>(null);

  const venueTz = venue?.timezone ?? null;
  const showOverrideAffordances = IS_ADMIN_HOST;

  const reload = useCallback(async () => {
    setLoadError(null);
    try {
      const r = await api.get<DeclaredShowPeriodsResponse>(
        `/api/windows/${encodeURIComponent(windowId)}/show-periods`,
      );
      setPeriods(r.periods);
    } catch (e) {
      setLoadError(apiErrorMessage(e, "Could not load show windows."));
      setPeriods([]);
    } finally {
      setLoading(false);
    }
  }, [windowId]);

  useEffect(() => {
    setLoading(true);
    reload();
  }, [reload]);

  // [Clear] fires the PATCH inline (no dialog) and drops the period out of
  // the session-overridden set so the row reverts to the NULL display state
  // until the next roster materialization tick re-detects.
  const handleClear = useCallback(
    async (periodId: string) => {
      setClearingId(periodId);
      try {
        await setShowStartOverride(periodId, null);
        setSessionOverridden((prev) => {
          if (!prev.has(periodId)) return prev;
          const next = new Set(prev);
          next.delete(periodId);
          return next;
        });
        await reload();
      } catch (err) {
        // Mirror the dialog's inline error UX with a one-shot loadError;
        // operator can retry from the row.
        setLoadError(apiErrorMessage(err, "Could not clear the show-start override."));
      } finally {
        setClearingId(null);
      }
    },
    [reload],
  );

  // Operator de-select: flips show_status between 'declared' and 'excluded'.
  // Excluded periods are skipped by the roster evaluator AND the auto-detector
  // (expire_stale_declarations) so an operator's "this had a load test, don't
  // count it" decision is durable. Triggers a roster refresh on the server
  // so show_condition_max_a recomputes immediately.
  const [excludingId, setExcludingId] = useState<string | null>(null);
  const handleExcludeToggle = useCallback(
    async (period: DeclaredShowPeriod) => {
      const targetExcluded = period.show_status !== "excluded";
      setExcludingId(period.period_id);
      try {
        await api.patch(
          `/api/show-periods/${encodeURIComponent(period.period_id)}/exclude`,
          { excluded: targetExcluded },
        );
        await reload();
      } catch (err) {
        setLoadError(apiErrorMessage(
          err,
          targetExcluded
            ? "Could not exclude the show period from sizing."
            : "Could not re-include the show period in sizing.",
        ));
      } finally {
        setExcludingId(null);
      }
    },
    [reload],
  );

  const handleOverrideDone = useCallback(
    async (periodId: string, ts: string | null) => {
      // ts==null can't happen via the dialog (Save only sets non-null) but
      // keep the signature future-proof — only mark overridden when actually
      // set.
      if (ts !== null) {
        setSessionOverridden((prev) => {
          if (prev.has(periodId)) return prev;
          const next = new Set(prev);
          next.add(periodId);
          return next;
        });
      }
      await reload();
    },
    [reload],
  );

  // Stable derived view so the per-row render is straightforward — each
  // period is annotated with its `overridden` session flag.
  const annotatedPeriods = useMemo(
    () => periods.map((p) => ({
      period: p,
      overridden: sessionOverridden.has(p.period_id),
    })),
    [periods, sessionOverridden],
  );

  return (
    <Card>
      <CardHeader>
        <CardTitle>Show windows</CardTitle>
        <p className="text-xs text-muted-foreground mt-1">
          Declare each show day's doors (and optional curfew). The window —
          derived server-side — feeds the house-feed load roster.
        </p>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* Venue timezone is a hard prerequisite — doors/curfew are
            venue-local wall-clock and the server derives the window in that
            zone. Prompt to set it rather than guessing. */}
        {!venue ? (
          <div className="text-sm text-muted-foreground py-2">
            Assign a venue to this event first — show windows are entered in
            venue-local time.
          </div>
        ) : !venueTz ? (
          <div className="rounded-md border border-amber-500/30 bg-amber-500/10 p-3 text-sm">
            <div className="font-medium text-amber-500">
              Set the venue timezone first
            </div>
            <p className="text-muted-foreground mt-1">
              Doors and curfew are entered in venue-local time, so{" "}
              <span className="font-medium">{venue.name}</span> needs an IANA
              timezone before a show window can be declared. Set it on the{" "}
              <a href="/admin/venues" className="underline hover:text-foreground">
                Venues admin page
              </a>
              .
            </p>
          </div>
        ) : (
          <>
            {loading ? (
              <div className="text-sm text-muted-foreground py-2">
                Loading show windows…
              </div>
            ) : loadError ? (
              <div className="space-y-2 py-2">
                <div className="text-destructive text-sm">{loadError}</div>
                <Button size="sm" variant="outline" onClick={() => reload()}>
                  Retry
                </Button>
              </div>
            ) : periods.length === 0 ? (
              <div className="text-sm text-muted-foreground py-2">
                No show days declared yet. Add a show day with its doors time —
                the derived window is what the load roster uses to compute
                show-condition peaks.
              </div>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Show day</TableHead>
                    <TableHead>Doors</TableHead>
                    <TableHead>Curfew</TableHead>
                    <TableHead>Derived window (venue-local)</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead className="w-44 text-right" />
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {annotatedPeriods.map(({ period: p, overridden }) => {
                    const kind = badgeKind(p);
                    const hasAnchor = p.actual_show_start_ts != null;
                    // The 3 v1 display states (drift state deferred):
                    //   - NULL → "not yet computed" + [Override]
                    //   - non-NULL + not session-overridden → "+Xmin from doors" + [Override]
                    //   - non-NULL + session-overridden     → "+Xmin from doors, operator override" + [Edit] [Clear]
                    return (
                      <Fragment key={p.period_id}>
                        <TableRow>
                          <TableCell className="font-mono text-xs">
                            {p.show_day}
                          </TableCell>
                          <TableCell className="text-xs tabular-nums">
                            {isoToClock(p.doors_at, venueTz)}
                          </TableCell>
                          <TableCell className="text-xs tabular-nums">
                            {p.curfew_at ? isoToClock(p.curfew_at, venueTz) : "—"}
                          </TableCell>
                          <TableCell className="text-xs tabular-nums text-muted-foreground">
                            {isoToDateClock(p.started_at, venueTz)}
                            {" - "}
                            {isoToDateClock(p.ended_at, venueTz)}
                          </TableCell>
                          <TableCell>
                            <LifecycleBadge kind={kind} />
                          </TableCell>
                          <TableCell className="text-right space-x-1">
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={() => setEditDoorsTarget(p)}
                            >
                              Edit doors
                            </Button>
                            {kind !== "no_show" && (
                              <Button
                                size="sm"
                                variant="ghost"
                                onClick={() => setTightenTarget(p)}
                              >
                                Tighten
                              </Button>
                            )}
                            {kind !== "no_show" && (
                              <Button
                                size="sm"
                                variant="ghost"
                                disabled={excludingId === p.period_id}
                                onClick={() => handleExcludeToggle(p)}
                                title={
                                  p.show_status === "excluded"
                                    ? "Re-include this show period in sizing (planner show_condition_max)"
                                    : "Exclude this show period from sizing — e.g. a load test inside the declared window inflating the planner peak"
                                }
                              >
                                {excludingId === p.period_id
                                  ? "…"
                                  : p.show_status === "excluded"
                                    ? "Re-include"
                                    : "Exclude"}
                              </Button>
                            )}
                            {kind !== "no_show" && (
                              <Button
                                size="sm"
                                variant="ghost"
                                asChild
                                title="Open the show report for this period (peak loads, decile histogram, inrush events, recurring patterns)"
                              >
                                <a
                                  href={showReportUrl(p.period_id)}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                >
                                  Show report ↗
                                </a>
                              </Button>
                            )}
                          </TableCell>
                        </TableRow>
                        {showOverrideAffordances && (
                          <TableRow className="border-t-0 hover:bg-transparent">
                            <TableCell
                              colSpan={6}
                              className="pt-0 pb-2 text-xs text-muted-foreground"
                            >
                              <div className="flex flex-wrap items-center gap-2 pl-2 border-l-2 border-muted ml-1">
                                <span className="font-medium text-foreground/80">
                                  Show-start:
                                </span>
                                {hasAnchor ? (
                                  <span className="tabular-nums">
                                    {formatShowStartLabel(
                                      p.actual_show_start_ts as string,
                                      p.doors_at,
                                      venueTz,
                                      overridden,
                                    )}
                                  </span>
                                ) : (
                                  <span>
                                    not yet computed (auto-detect runs on next
                                    roster materialization)
                                  </span>
                                )}
                                <div className="ml-auto flex items-center gap-1">
                                  <Button
                                    size="sm"
                                    variant="ghost"
                                    onClick={() => setOverrideTarget(p)}
                                  >
                                    {overridden ? "Edit" : "Override"}
                                  </Button>
                                  {overridden && (
                                    <Button
                                      size="sm"
                                      variant="ghost"
                                      disabled={clearingId === p.period_id}
                                      onClick={() => handleClear(p.period_id)}
                                    >
                                      {clearingId === p.period_id
                                        ? "Clearing…"
                                        : "Clear"}
                                    </Button>
                                  )}
                                </div>
                              </div>
                            </TableCell>
                          </TableRow>
                        )}
                      </Fragment>
                    );
                  })}
                </TableBody>
              </Table>
            )}

            {!loading && !loadError && (
              <AddShowDayForm
                windowId={windowId}
                venueTz={venueTz}
                onAdded={reload}
              />
            )}
          </>
        )}
      </CardContent>

      {tightenTarget && venueTz && (
        <TightenDialog
          period={tightenTarget}
          venueTz={venueTz}
          onClose={() => setTightenTarget(null)}
          onDone={reload}
        />
      )}
      {editDoorsTarget && venueTz && (
        <EditDoorsDialog
          period={editDoorsTarget}
          venueTz={venueTz}
          onClose={() => setEditDoorsTarget(null)}
          onDone={reload}
        />
      )}
      {overrideTarget && venueTz && (
        <ShowStartOverrideDialog
          period={overrideTarget}
          venueTz={venueTz}
          onClose={() => setOverrideTarget(null)}
          onDone={(ts) => handleOverrideDone(overrideTarget.period_id, ts)}
        />
      )}
    </Card>
  );
}
